import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Store,
  RefreshCw,
  UserCheck,
  UserX,
  Clock,
  Camera,
  X,
  Inbox,
  AlertCircle,
  DoorOpen,
  Radio,
  ExternalLink,
  ImageOff,
  ChevronRight,
  CalendarDays,
} from "lucide-react";
import axios from "../../utils/axiosDash";

/* ============================================================================
   1. CONFIG
   ========================================================================== */

const STORE_STORAGE_KEY = "selectedStore";
const ALL_STORES = "All Stores";
const REFRESH_MS = 60 * 1000;
const STALE_AFTER_MIN = 10;

const ENDPOINTS = {
  alerts: "/get-empire-cashier-alerts",
  logs: "/get-empire-cashier-logs",
};

const ALERT_TYPES = {
  absent: {
    label: "Counter Unattended",
    hint: "Nobody was at the counter",
    icon: UserX,
    chip: "bg-rose-50 text-rose-700 ring-rose-200",
    iconBox: "bg-rose-50 text-rose-600",
  },
  drawer: {
    label: "Cash drawer opened",
    hint: "Drawer activity detected",
    icon: DoorOpen,
    chip: "bg-amber-50 text-amber-700 ring-amber-200",
    iconBox: "bg-amber-50 text-amber-600",
  },
};

const STATE_STYLE = {
  present: "bg-emerald-400",
  absent: "bg-rose-400",
  nodata: "bg-slate-200",
};

/* ---- shifts ---- */
const SHIFT_KEYS = ["shift1", "shift2"];
const SHIFT_LABEL = { shift1: "Shift 1", shift2: "Shift 2" };
const SHIFT_STYLE = {
  shift1: { chip: "bg-amber-50 text-amber-700 ring-amber-200", icon: "bg-amber-100 text-amber-600" },
  shift2: { chip: "bg-indigo-50 text-indigo-700 ring-indigo-200", icon: "bg-indigo-100 text-indigo-600" },
};

// Fallback windows (24h). Real timings come from the logs API (`shiftTimings`, per store).
// Start inclusive, end exclusive.
// Shift 1: 11:00 AM -> 7:00 PM (same day)   |   Shift 2: 7:00 PM -> 2:30 AM (next day)
const DEFAULT_WINDOWS = {
  shift1: { start: "11:00", end: "19:00" },
  shift2: { start: "19:00", end: "02:30" },
};

/* ============================================================================
   2. TIME HANDLING (NO timezone shift — everything is shown exactly as stored)
   ========================================================================== */

const WALL_RE = /(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/;
const pad = (n) => String(n).padStart(2, "0");

const safeDate = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "string") {
    const m = v.match(WALL_RE);
    if (m) {
      return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0)));
    }
  }
  const d = new Date(v?.$date || v);
  return isNaN(d.getTime()) ? null : d;
};

// "now" on the same as-stored basis as the data
const wallNow = () => {
  const n = new Date();
  return new Date(
    Date.UTC(n.getFullYear(), n.getMonth(), n.getDate(), n.getHours(), n.getMinutes(), n.getSeconds())
  );
};

// today as YYYY-MM-DD (browser local time) for the date filter
const todayStr = () => {
  const n = new Date();
  return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`;
};

const dayKey = (d) => d.toISOString().slice(0, 10);

// "2026-10-29" -> "2026-10-30"
const nextDateKey = (key) => {
  const [y, m, d] = key.split("-").map(Number);
  return dayKey(new Date(Date.UTC(y, m - 1, d + 1)));
};

// shift 1 -> [date]; shift 2 / all -> [date, next day] (shift 2 runs past midnight)
const datesForShift = (dateKey, shiftValue) =>
  !dateKey ? [] : shiftValue === "shift1" ? [dateKey] : [dateKey, nextDateKey(dateKey)];

/* ============================================================================
   3. SHIFT HELPERS
   ========================================================================== */

// "19:00" -> 1140
const hhmmToMin = (s) => {
  if (!s || typeof s !== "string") return null;
  const [h, m] = s.split(":").map(Number);
  if (isNaN(h)) return null;
  return h * 60 + (m || 0);
};

// 1140 -> "7:00 PM" (wraps past 24h)
const minToLabel = (mins) => {
  const total = ((mins % 1440) + 1440) % 1440;
  const h = Math.floor(total / 60);
  return `${h % 12 === 0 ? 12 : h % 12}:${pad(total % 60)} ${h >= 12 ? "PM" : "AM"}`;
};

const fmt12 = (s) => {
  const m = hhmmToMin(s);
  return m === null ? "—" : minToLabel(m);
};

const crossesMidnight = (t) => {
  const s = hhmmToMin(t?.start);
  const e = hhmmToMin(t?.end);
  return s !== null && e !== null && e < s;
};

const shiftRangeLabel = (t) =>
  t ? `${fmt12(t.start)} – ${fmt12(t.end)}${crossesMidnight(t) ? " (next day)" : ""}` : "";

// length of a shift in minutes (handles midnight crossing)
const shiftMinutes = (t) => {
  const s = hhmmToMin(t?.start);
  const e = hhmmToMin(t?.end);
  if (s === null || e === null) return 0;
  const diff = (e - s + 1440) % 1440;
  return diff === 0 ? 1440 : diff;
};

const isValidTiming = (t) => hhmmToMin(t?.start) !== null && hhmmToMin(t?.end) !== null;

// windows of one store from the API's shiftTimings (falls back to defaults)
const windowsFor = (timings, store) => {
  const t = timings?.[store];
  return t && isValidTiming(t.shift1) && isValidTiming(t.shift2) ? t : DEFAULT_WINDOWS;
};

// which shift does this moment fall in? (null = outside both shifts)
const shiftOfTime = (d, windows = DEFAULT_WINDOWS) => {
  if (!d || Number.isNaN(d.getTime())) return null;
  const mins = d.getUTCHours() * 60 + d.getUTCMinutes();
  for (const key of SHIFT_KEYS) {
    const start = hhmmToMin(windows[key]?.start);
    const end = hhmmToMin(windows[key]?.end);
    if (start === null || end === null) continue;
    const inside = start < end ? mins >= start && mins < end : mins >= start || mins < end;
    if (inside) return key;
  }
  return null;
};

// a night shift belongs to the date on which it STARTED
// (a check at 12:30 AM on the 30th belongs to the 29th's Shift 2)
const shiftDateKeyOf = (d, shiftKey, windows = DEFAULT_WINDOWS) => {
  const t = windows[shiftKey];
  if (crossesMidnight(t)) {
    const mins = d.getUTCHours() * 60 + d.getUTCMinutes();
    if (mins < hhmmToMin(t.start)) return dayKey(new Date(d.getTime() - 86400000));
  }
  return dayKey(d);
};

/* ============================================================================
   4. NORMALIZERS
   ========================================================================== */

function toState(v) {
  if (v === true || v === 1) return "present";
  if (v === false || v === 0) return "absent";
  const s = String(v ?? "").toLowerCase();
  if (s.includes("absent") || s === "a" || s === "false") return "absent";
  if (s.includes("present") || s === "p" || s === "true") return "present";
  return "nodata";
}

/*
  Logs API response:
    { success, date, shift1: [...minute records], shift2: [...minute records], shiftTimings: {...} }
  Both arrays are merged and every minute is placed into a shift by its OWN timestamp using the
  store's shift timings — so it doesn't matter which array the backend put it in.
  Duplicates (same store + camera + minute) are removed.
*/
function flattenLogs(records, timings) {
  const seen = new Set();
  const out = [];

  const push = (store, channel, cameraIp, item) => {
    const raw = safeDate(item.minute ?? item.checkTime);
    if (!raw) return;
    const time = new Date(Math.floor(raw.getTime() / 60000) * 60000); // floor to the minute

    const state = toState(item.cashierState);
    if (state === "nodata") return;

    const windows = windowsFor(timings, store);
    const shiftKey = shiftOfTime(time, windows);
    if (!shiftKey) return; // outside configured shifts

    const id = `${store}|${cameraIp}|${channel}|${time.getTime()}`;
    if (seen.has(id)) return;
    seen.add(id);

    out.push({
      store,
      channel,
      cameraIp,
      time,
      shift: shiftKey,
      dateKey: shiftDateKeyOf(time, shiftKey, windows),
      state,
      alert: !!item.alertGenerated,
      streak: item.consecutiveAbsentChecks ?? 0,
      postgresCreatedAt: safeDate(item.postgresCreatedAt),
    });
  };

  (records || []).forEach((doc) => {
    const store = doc.restaurant ?? doc.store ?? "—";
    const channel = doc.channel ?? null;
    const cameraIp = doc.cameraIp ?? null;

    if (Array.isArray(doc.minuteData)) {
      doc.minuteData.forEach((m) => push(store, channel, cameraIp, m)); // supports hourly docs too
    } else {
      push(store, channel, cameraIp, doc);
    }
  });

  return out;
}

function classifyAlert(raw) {
  const text = [raw.useCase, raw.alertType, raw.type, raw.cashierState, raw.message]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  if (raw.drawerState || /drawer|cash drawer/.test(text)) return "drawer";
  if (/(absent|away|missing|unattended|not.?present|no.?cashier|counter)/.test(text)) return "absent";
  return "drawer";
}

function normalizeAlert(raw, i) {
  const type = classifyAlert(raw);
  const drawerState = raw.drawerState ? String(raw.drawerState).toLowerCase() : null;
  return {
    id: raw._id ?? raw.postgresId ?? `alert-${i}`,
    type,
    store: raw.restaurant ?? raw.store ?? "—",
    time: safeDate(raw.checkTime) ?? safeDate(raw.postgresCreatedAt) ?? wallNow(),
    message:
      raw.message ?? (type === "drawer" ? `Drawer ${drawerState ?? "activity"}` : ALERT_TYPES[type].hint),
    useCase: raw.useCase ?? null,
    drawerState,
    channel: raw.channel ?? null,
    cameraIp: raw.cameraIp ?? null,
    image: raw.imagePath ?? raw.imageUrl ?? raw.image ?? null,
  };
}

/* ============================================================================
   5. DISPLAY HELPERS
   ========================================================================== */

const fmtTime = (d) =>
  d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "UTC" });
const fmtDay = (d) => d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", timeZone: "UTC" });

// 125 -> "2 hr 5 min"
const fmtMins = (m) => {
  if (!m) return "0 min";
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (!h) return `${r} min`;
  return r ? `${h} hr ${r} min` : `${h} hr`;
};

function timeAgo(date) {
  const mins = Math.max(0, Math.round((wallNow().getTime() - date.getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  return h < 24 ? `${h} hr ago` : `${Math.floor(h / 24)} d ago`;
}

function dayLabel(d) {
  const now = wallNow();
  const k = dayKey(d);
  if (k === dayKey(now)) return "Today";
  if (k === dayKey(new Date(now.getTime() - 86400000))) return "Yesterday";
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", timeZone: "UTC" });
}

const rateTone = (pct) =>
  pct === null
    ? { text: "text-slate-400", bg: "bg-slate-100" }
    : pct >= 80
    ? { text: "text-emerald-600", bg: "bg-emerald-50" }
    : pct >= 50
    ? { text: "text-amber-600", bg: "bg-amber-50" }
    : { text: "text-rose-600", bg: "bg-rose-50" };

/*
  Builds every hour of one shift for one store on one date.
  Shift 2 (19:00 -> 02:30) => 7 PM, 8 PM, 9 PM, 10 PM, 11 PM, 12 AM, 1 AM and 2 AM (only 30 min).
  Each hour has 60 minute-slots; slots outside the shift (e.g. 2:30–2:59) are flagged inShift:false.
*/
function buildShiftHours(shiftKey, windows, dateKey, minuteMap, storeAlerts) {
  const t = windows[shiftKey];
  const start = hhmmToMin(t.start);
  const end = start + shiftMinutes(t);
  const [y, m, d] = dateKey.split("-").map(Number);
  const dayMs = Date.UTC(y, m - 1, d);

  const out = [];
  let cur = start;
  while (cur < end) {
    const hourIdx = Math.floor(cur / 60);
    const chunkEnd = Math.min((hourIdx + 1) * 60, end);
    const offset = cur % 60;
    const cap = chunkEnd - cur;
    const hourStart = new Date(dayMs + hourIdx * 3600000);
    const hourEnd = new Date(hourStart.getTime() + 3600000);

    const slots = Array.from({ length: 60 }, (_, mm) => {
      const inShift = mm >= offset && mm < offset + cap;
      const c = inShift ? minuteMap?.get(hourStart.getTime() + mm * 60000) : null;
      return { minute: mm, inShift, state: c ? c.state : "nodata", alert: c ? c.alert : false };
    });

    const present = slots.filter((s) => s.state === "present").length;
    const absent = slots.filter((s) => s.state === "absent").length;
    const tracked = present + absent;

    out.push({
      id: `${shiftKey}-${hourStart.getTime()}`,
      shift: shiftKey,
      hourStart,
      hourEnd,
      cap,
      slots,
      present,
      absent,
      tracked,
      noData: Math.max(cap - tracked, 0),
      alerts: storeAlerts.filter((a) => a.shift === shiftKey && a.time >= hourStart && a.time < hourEnd).length,
      label: `${minToLabel(cur)} – ${minToLabel(chunkEnd)}`,
    });
    cur = chunkEnd;
  }
  return out;
}

/* ============================================================================
   6. MAIN PAGE
   ========================================================================== */

export default function CashCounterDashboard() {
  const storeName = localStorage.getItem(STORE_STORAGE_KEY) || ALL_STORES;
  const isAllStores = storeName === ALL_STORES;

  const [selectedDate, setSelectedDate] = useState(todayStr());
  const [shift, setShift] = useState("all"); // all | shift1 | shift2
  const isToday = selectedDate === todayStr();

  const [rawAlerts, setRawAlerts] = useState([]);
  const [checks, setChecks] = useState([]);
  const [shiftTimings, setShiftTimings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [selectedHourId, setSelectedHourId] = useState(null);
  const [activeAlert, setActiveAlert] = useState(null);
  const [drillStore, setDrillStore] = useState(null);
  const reqId = useRef(0);

  const visibleShifts = useMemo(() => (shift === "all" ? SHIFT_KEYS : [shift]), [shift]);

  // windows used for dropdown labels: selected store -> first store from API -> defaults
  const displayWindows = useMemo(() => {
    const own = shiftTimings[storeName];
    if (own && isValidTiming(own.shift1) && isValidTiming(own.shift2)) return own;
    const first = Object.values(shiftTimings).find((t) => t && isValidTiming(t.shift1) && isValidTiming(t.shift2));
    return first || DEFAULT_WINDOWS;
  }, [shiftTimings, storeName]);

  const shiftRanges = useMemo(
    () => ({
      shift1: shiftRangeLabel(displayWindows.shift1),
      shift2: shiftRangeLabel(displayWindows.shift2),
    }),
    [displayWindows]
  );

  const fetchData = useCallback(
    async (silent = false) => {
      const id = ++reqId.current;
      if (!silent) setLoading(true);
      try {
        const days = datesForShift(selectedDate, shift);
        const payload = {
          store: storeName,
          date: selectedDate,
          shift, // "all" | "shift1" | "shift2"
          dates: days,
          startDate: days[0],
          endDate: days[days.length - 1],
        };
        const [alertRes, logRes] = await Promise.all([
          axios.post(ENDPOINTS.alerts, payload),
          axios.post(ENDPOINTS.logs, payload),
        ]);
        if (id !== reqId.current) return; // a newer request replaced this one

        const body = logRes.data || {};
        if (body.success === false) throw new Error(body.message || "Could not load cashier logs");

        const timings = body.shiftTimings || {};
        setShiftTimings(timings);
        setChecks(
          flattenLogs(
            [...(body.shift1 || []), ...(body.shift2 || [])],
            timings
          )
        );
        setRawAlerts((alertRes.data?.data ?? []).map(normalizeAlert));
        setUpdatedAt(wallNow());
        setError("");
      } catch (e) {
        if (id !== reqId.current) return;
        setError(e?.response?.data?.message || e.message || "Could not load cashier data");
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    },
    [storeName, selectedDate, shift]
  );

  // fetch on mount and whenever store / date / shift changes; auto-refresh only for today
  useEffect(() => {
    fetchData();
    if (!isToday) return undefined;
    const t = setInterval(() => fetchData(true), REFRESH_MS);
    return () => clearInterval(t);
  }, [fetchData, isToday]);

  useEffect(() => {
    setDrillStore(null);
    setSelectedHourId(null);
  }, [storeName]);

  useEffect(() => {
    setSelectedHourId(null);
  }, [selectedDate, shift]);

  const handleDateChange = (value) => setSelectedDate(value || todayStr());

  /* -------------------- alerts for the selected date + shift -------------------- */

  // an alert belongs to the shift it happened in (and the date that shift STARTED on)
  const alerts = useMemo(
    () =>
      rawAlerts
        .map((a) => {
          const windows = windowsFor(shiftTimings, a.store);
          const sk = shiftOfTime(a.time, windows);
          return { ...a, shift: sk, shiftDate: sk ? shiftDateKeyOf(a.time, sk, windows) : dayKey(a.time) };
        })
        .filter((a) => {
          if (a.shift) return visibleShifts.includes(a.shift) && a.shiftDate === selectedDate;
          return shift === "all" && a.shiftDate === selectedDate; // outside both shift windows
        })
        .sort((x, y) => y.time - x.time),
    [rawAlerts, shiftTimings, visibleShifts, shift, selectedDate]
  );

  /* -------------------- per-store, shift-wise aggregation -------------------- */

  const storeSummaries = useMemo(() => {
    const stores = new Set();
    if (!isAllStores) stores.add(storeName);
    checks.forEach((c) => stores.add(c.store));
    alerts.forEach((a) => stores.add(a.store));

    // store -> shift -> (minute ms -> check) for the selected date
    const maps = new Map();
    const lastByStore = new Map();
    checks.forEach((c) => {
      const last = lastByStore.get(c.store);
      if (!last || c.time > last.time) lastByStore.set(c.store, c);

      if (c.dateKey !== selectedDate || !visibleShifts.includes(c.shift)) return;
      if (!maps.has(c.store)) maps.set(c.store, { shift1: new Map(), shift2: new Map() });
      maps.get(c.store)[c.shift].set(c.time.getTime(), c);
    });

    const now = wallNow().getTime();
    const list = [...stores].map((store) => {
      const windows = windowsFor(shiftTimings, store);
      const storeAlerts = alerts.filter((a) => a.store === store);

      const shifts = visibleShifts.map((key) => {
        const hours = buildShiftHours(key, windows, selectedDate, maps.get(store)?.[key], storeAlerts);
        const present = hours.reduce((n, h) => n + h.present, 0);
        const absent = hours.reduce((n, h) => n + h.absent, 0);
        return {
          key,
          label: SHIFT_LABEL[key],
          range: shiftRangeLabel(windows[key]),
          totalMins: shiftMinutes(windows[key]),
          hasData: present + absent > 0,
          present,
          absent,
          alerts: storeAlerts.filter((a) => a.shift === key).length,
          hours,
        };
      });

      const present = shifts.reduce((n, s) => n + s.present, 0);
      const absent = shifts.reduce((n, s) => n + s.absent, 0);
      const last = lastByStore.get(store) || null;
      const live = last
        ? { state: last.state, at: last.time, stale: (now - last.time.getTime()) / 60000 > STALE_AFTER_MIN }
        : null;

      return { store, shifts, present, absent, tracked: present + absent, alerts: storeAlerts.length, live, last };
    });

    // freshest first
    return list.sort((a, b) => (b.live?.at?.getTime() ?? 0) - (a.live?.at?.getTime() ?? 0));
  }, [checks, alerts, shiftTimings, visibleShifts, selectedDate, isAllStores, storeName]);

  const overallTracked = useMemo(() => storeSummaries.reduce((n, s) => n + s.tracked, 0), [storeSummaries]);

  const effectiveDrillStore = useMemo(() => {
    if (!isAllStores) return storeName;
    if (drillStore && storeSummaries.some((s) => s.store === drillStore)) return drillStore;
    return storeSummaries[0]?.store ?? null;
  }, [isAllStores, storeName, drillStore, storeSummaries]);

  /* -------------------- focused store -------------------- */

  const focused = useMemo(
    () => storeSummaries.find((s) => s.store === effectiveDrillStore) || null,
    [storeSummaries, effectiveDrillStore]
  );

  const allHours = useMemo(() => (focused ? focused.shifts.flatMap((s) => s.hours) : []), [focused]);

  const selectedHour =
    allHours.find((h) => h.id === selectedHourId) ||
    [...allHours].reverse().find((h) => h.tracked > 0) ||
    allHours[0] ||
    null;

  const totals = useMemo(
    () => ({
      present: focused?.present ?? 0,
      absent: focused?.absent ?? 0,
      tracked: focused?.tracked ?? 0,
    }),
    [focused]
  );

  const liveState = focused?.live ?? null;

  const scopedAlerts = useMemo(
    () => (isAllStores ? alerts.filter((a) => a.store === effectiveDrillStore) : alerts),
    [isAllStores, effectiveDrillStore, alerts]
  );

  const groupedAlerts = useMemo(() => {
    const groups = [];
    scopedAlerts.forEach((a) => {
      const label = dayLabel(a.time);
      const last = groups[groups.length - 1];
      if (last && last.label === label) last.items.push(a);
      else groups.push({ label, items: [a] });
    });
    return { groups, total: scopedAlerts.length };
  }, [scopedAlerts]);

  const staffedNow = useMemo(
    () => storeSummaries.filter((s) => s.live && !s.live.stale && s.live.state === "present").length,
    [storeSummaries]
  );

  const camera = focused?.last ?? scopedAlerts[0];
  const shiftHint = shift === "all" ? "All shifts" : SHIFT_LABEL[shift];

  /* -------------------- render -------------------- */

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-slate-50 to-sky-50/40 text-slate-800">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <Header
          camera={camera}
          liveState={liveState}
          updatedAt={updatedAt}
          loading={loading}
          onRefresh={() => fetchData()}
          isAllStores={isAllStores}
          staffedNow={staffedNow}
          totalStores={storeSummaries.length}
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
          isToday={isToday}
          shift={shift}
          onShiftChange={setShift}
          shiftRanges={shiftRanges}
        />

        {error && (
          <div className="mt-5 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <AlertCircle size={16} className="shrink-0" />
            <span className="flex-1">{error}</span>
            <button onClick={() => fetchData()} className="font-medium underline">
              Try again
            </button>
          </div>
        )}

        {/* KPIs */}
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {isAllStores && isToday ? (
            <KpiCard
              icon={Store}
              tone="sky"
              label="Stores Staffed Now"
              value={`${staffedNow} / ${storeSummaries.length}`}
              hint="Currently at the counter"
              loading={loading}
            />
          ) : (
            <KpiCard
              icon={Clock}
              tone="sky"
              label="Minutes tracked"
              value={fmtMins(isAllStores ? overallTracked : totals.tracked)}
              hint={isAllStores ? `All stores · ${shiftHint}` : shiftHint}
              loading={loading}
            />
          )}
          <KpiCard
            icon={UserCheck}
            tone="emerald"
            label="Minutes present"
            value={fmtMins(totals.present)}
            hint={`${isAllStores && effectiveDrillStore ? `${effectiveDrillStore} · ` : ""}${shiftHint}`}
            loading={loading}
          />
          <KpiCard
            icon={UserX}
            tone="rose"
            label="Minutes absent"
            value={fmtMins(totals.absent)}
            hint={`${isAllStores && effectiveDrillStore ? `${effectiveDrillStore} · ` : ""}${shiftHint}`}
            loading={loading}
          />
          <KpiCard
            icon={DoorOpen}
            tone="amber"
            label="Alerts raised"
            value={focused?.alerts ?? 0}
            hint={`${isAllStores && effectiveDrillStore ? `${effectiveDrillStore} · ` : ""}${shiftHint}`}
            loading={loading}
          />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* LEFT */}
          <div className="space-y-6 lg:col-span-7">
            {isAllStores && (
              <Card
                title="Per-Store Overview"
                subtitle={
                  storeSummaries.length
                    ? "Click a store to view its shifts and hourly details below"
                    : "No stores reported for this date"
                }
              >
                {loading && !storeSummaries.length ? (
                  <SkeletonRows />
                ) : storeSummaries.length ? (
                  <ul className="space-y-1">
                    {storeSummaries.map((s) => (
                      <StoreSummaryRow
                        key={s.store}
                        summary={s}
                        active={s.store === effectiveDrillStore}
                        isToday={isToday}
                        onClick={() => {
                          setDrillStore(s.store);
                          setSelectedHourId(null);
                        }}
                      />
                    ))}
                  </ul>
                ) : (
                  <EmptyState text="No stores reporting for the selected date." />
                )}
              </Card>
            )}

            {/* Shift summary of the focused store */}
            <Card
              title="Shift Summary"
              subtitle={`${effectiveDrillStore ?? "—"} · ${selectedDate}${
                shift !== "shift1" ? ` (Shift 2 runs into ${nextDateKey(selectedDate)}, up to ${fmt12(displayWindows.shift2.end)})` : ""
              }`}
            >
              {loading && !focused ? (
                <SkeletonRows />
              ) : focused ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {focused.shifts.map((s) => (
                    <ShiftSummaryCard key={s.key} data={s} />
                  ))}
                </div>
              ) : (
                <EmptyState text="No shift data for the selected date." />
              )}
            </Card>

            <Card
              title="Minute-by-Minute Presence"
              subtitle={
                selectedHour
                  ? `${isAllStores ? `${effectiveDrillStore} · ` : ""}${SHIFT_LABEL[selectedHour.shift]} · ${fmtDay(
                      selectedHour.hourStart
                    )} · ${selectedHour.label}`
                  : "No hour selected"
              }
              right={<Legend />}
            >
              {selectedHour ? (
                <MinuteGrid log={selectedHour} />
              ) : (
                <EmptyState text="No presence data for the selected date." />
              )}
            </Card>
          </div>

          {/* RIGHT */}
          <div className="space-y-6 lg:col-span-5">
            <Card title="Alerts" subtitle={`${groupedAlerts.total} shown · ${shiftHint}`}>
              {loading && !scopedAlerts.length ? (
                <SkeletonRows />
              ) : groupedAlerts.total ? (
                <div className="-mx-2 max-h-[560px] space-y-4 overflow-y-auto px-2">
                  {groupedAlerts.groups.map((g) => (
                    <div key={g.label}>
                      <p className="sticky top-0 z-10 bg-white/90 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400 backdrop-blur">
                        {g.label} · {g.items.length}
                      </p>
                      <ul className="mt-1 space-y-1">
                        {g.items.map((a) => (
                          <AlertItem key={a.id} alert={a} onClick={() => setActiveAlert(a)} />
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState text="No alerts for the selected date and shift." />
              )}
            </Card>
          </div>
        </div>

        {/* HOURLY TIMELINE — full width: Shift 1 on the left, Shift 2 on the right */}
        <div className="mt-6">
          <Card
            title="Hourly Timeline"
            subtitle={`${effectiveDrillStore ?? "—"} · hour by hour, shift by shift — click an hour to see its minutes`}
          >
            {loading && !focused ? (
              <SkeletonRows />
            ) : focused && focused.shifts.length ? (
              <div
                className={`grid grid-cols-1 gap-6 ${
                  focused.shifts.length > 1 ? "lg:grid-cols-2 lg:gap-8" : ""
                }`}
              >
                {focused.shifts.map((s) => (
                  <div key={s.key} className="min-w-0">
                    <div className="flex items-center justify-between py-1.5">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${SHIFT_STYLE[s.key].chip}`}
                      >
                        <Clock size={12} /> {s.label}
                      </span>
                      <span className="text-[11px] text-slate-400">{s.range}</span>
                    </div>
                    <ul className="mt-1 space-y-1">
                      {s.hours.map((h) => (
                        <HourRow
                          key={h.id}
                          log={h}
                          active={selectedHour?.id === h.id}
                          onClick={() => setSelectedHourId(h.id)}
                        />
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState text="No hourly logs found for this store on the selected date." />
            )}
          </Card>
        </div>
      </div>

      {activeAlert && <AlertPreviewModal alert={activeAlert} onClose={() => setActiveAlert(null)} />}
    </div>
  );
}

/* ============================================================================
   7. SECTIONS
   ========================================================================== */

function Header({
  camera,
  liveState,
  updatedAt,
  loading,
  onRefresh,
  isAllStores,
  staffedNow,
  totalStores,
  selectedDate,
  onDateChange,
  isToday,
  shift,
  onShiftChange,
  shiftRanges,
}) {
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex items-center gap-4">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-100 to-indigo-100 text-sky-600 shadow-sm">
          <Store size={22} />
        </span>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Cash Counter Monitoring</h1>
          {isAllStores && totalStores > 0 && (
            <p className="mt-0.5 text-xs text-slate-400">
              {totalStores} {totalStores === 1 ? "store" : "stores"} reporting
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <DateFilter value={selectedDate} onChange={onDateChange} isToday={isToday} />
        <ShiftFilter value={shift} onChange={onShiftChange} ranges={shiftRanges} />
        <button
          onClick={onRefresh}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
        >
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>
    </div>
  );
}

function DateFilter({ value, onChange, isToday }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-1.5 shadow-sm focus-within:ring-2 focus-within:ring-sky-300">
      <CalendarDays size={15} className="text-slate-400" />
      <input
        type="date"
        value={value}
        max={todayStr()}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Select date"
        className="bg-transparent text-sm font-medium text-slate-700 focus:outline-none"
      />
      {!isToday && (
        <button
          onClick={() => onChange(todayStr())}
          className="rounded-lg bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700 ring-1 ring-sky-200 hover:bg-sky-100"
        >
          Today
        </button>
      )}
    </div>
  );
}

function ShiftFilter({ value, onChange, ranges }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-1.5 shadow-sm focus-within:ring-2 focus-within:ring-sky-300">
      <Clock size={15} className="text-slate-400" />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Select shift"
        className="bg-transparent text-sm font-medium text-slate-700 focus:outline-none"
      >
        <option value="all">All shifts</option>
        {SHIFT_KEYS.map((k) => (
          <option key={k} value={k}>
            {SHIFT_LABEL[k]} ({ranges[k]})
          </option>
        ))}
      </select>
    </div>
  );
}

function LiveBadge({ liveState, isAllStores, staffedNow, totalStores, isToday }) {
  if (!isToday) {
    return (
      <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-500 ring-1 ring-slate-200">
        <Clock size={14} /> Historical view
      </span>
    );
  }

  if (isAllStores) {
    if (!totalStores) {
      return (
        <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-500">
          <Radio size={14} /> No live data
        </span>
      );
    }
    const all = staffedNow === totalStores;
    const none = staffedNow === 0;
    const tone = none
      ? "bg-rose-50 text-rose-700 ring-rose-200"
      : all
      ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
      : "bg-amber-50 text-amber-700 ring-amber-200";
    return (
      <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ring-1 ${tone}`}>
        <Radio size={14} />
        {staffedNow} of {totalStores} stores staffed
      </span>
    );
  }

  if (!liveState)
    return (
      <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-500">
        <Radio size={14} /> No live data
      </span>
    );
  if (liveState.stale)
    return (
      <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-500 ring-1 ring-slate-200">
        <Radio size={14} /> No recent data · last seen {timeAgo(liveState.at)}
      </span>
    );
  const present = liveState.state === "present";
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ring-1 ${
        present ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-rose-50 text-rose-700 ring-rose-200"
      }`}
    >
      <span className="relative flex h-2.5 w-2.5">
        <span
          className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${
            present ? "bg-emerald-400" : "bg-rose-400"
          }`}
        />
        <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${present ? "bg-emerald-500" : "bg-rose-500"}`} />
      </span>
      {present ? "Cashier at the counter" : "Counter Unattended"}
    </span>
  );
}

function KpiCard({ icon: Icon, tone, label, value, hint, loading }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-600",
    rose: "bg-rose-50 text-rose-600",
    sky: "bg-sky-50 text-sky-600",
    amber: "bg-amber-50 text-amber-600",
  };
  return (
    <div className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-sm transition hover:shadow-md sm:p-5">
      <div className="flex items-center gap-3">
        <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${tones[tone]}`}>
          <Icon size={17} />
        </span>
        <p className="text-sm font-medium text-slate-500">{label}</p>
      </div>
      {loading ? (
        <div className="mt-4 h-8 w-20 animate-pulse rounded-md bg-slate-100" />
      ) : (
        <p className="mt-4 text-3xl font-semibold tracking-tight text-slate-900">{value}</p>
      )}
      <p className="mt-1 truncate text-xs text-slate-400">{hint}</p>
    </div>
  );
}

function Card({ title, subtitle, right, children }) {
  return (
    <section className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-slate-400">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

/* ---- Shift bar + summary ------------------------------------------------- */

// Present / Absent / No-check split of a whole shift
function ShiftBar({ present, absent, total }) {
  const base = Math.max(total || 0, present + absent, 1);
  const none = Math.max(base - present - absent, 0);
  return (
    <div
      className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100"
      title={`${fmtMins(present)} present · ${fmtMins(absent)} absent · ${fmtMins(none)} no check`}
    >
      <span className="bg-emerald-400" style={{ width: `${(present / base) * 100}%` }} />
      <span className="bg-rose-400" style={{ width: `${(absent / base) * 100}%` }} />
    </div>
  );
}

function ShiftSummaryCard({ data }) {
  const checked = data.present + data.absent;
  const pct = checked ? Math.round((data.present / checked) * 100) : null;
  const tone = rateTone(pct);
  const style = SHIFT_STYLE[data.key];
  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${style.icon}`}>
            <Clock size={15} />
          </span>
          <div>
            <p className="text-sm font-semibold text-slate-800">{data.label}</p>
            <p className="text-[11px] text-slate-400">
              {data.range} · {fmtMins(data.totalMins)}
            </p>
          </div>
        </div>
        {data.hasData && (
          <span className={`rounded-md px-2 py-1 text-xs font-semibold ${tone.bg} ${tone.text}`}>
            {pct === null ? "—" : `${pct}% present`}
          </span>
        )}
      </div>

      {data.hasData ? (
        <>
          <ShiftBar present={data.present} absent={data.absent} total={data.totalMins} />
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            <span className="font-medium text-emerald-600">Present {fmtMins(data.present)}</span>
            <span className="font-medium text-rose-600">Absent {fmtMins(data.absent)}</span>
            <span className="font-medium text-amber-600">
              {data.alerts} alert{data.alerts === 1 ? "" : "s"}
            </span>
          </div>
        </>
      ) : (
        <p className="text-xs text-slate-400">No data for this shift.</p>
      )}
    </div>
  );
}

/* ---- Per-store overview row --------------------------------------------- */

function StoreSummaryRow({ summary, active, isToday, onClick }) {
  const { store, last, present, absent, alerts, live, shifts } = summary;
  const liveLabel = !isToday
    ? { text: "Historical data", tone: "bg-slate-100 text-slate-500 ring-slate-200" }
    : !live
    ? { text: "No data", tone: "bg-slate-100 text-slate-500 ring-slate-200" }
    : live.stale
    ? { text: "No recent data", tone: "bg-slate-100 text-slate-500 ring-slate-200" }
    : live.state === "present"
    ? { text: "Staffed", tone: "bg-emerald-50 text-emerald-700 ring-emerald-200" }
    : { text: "Unattended", tone: "bg-rose-50 text-rose-700 ring-rose-200" };

  return (
    <li>
      <button
        onClick={onClick}
        className={`flex w-full items-center gap-4 rounded-xl px-3 py-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 ${
          active ? "bg-sky-50 ring-1 ring-sky-200" : "hover:bg-slate-50"
        }`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-semibold text-slate-800">{store}</p>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${liveLabel.tone}`}>
              {liveLabel.text}
            </span>
          </div>
          <p className="mt-0.5 truncate text-[11px] text-slate-400">
            {last ? `Last check ${fmtTime(last.time)} · ${fmtDay(last.time)}` : "No checks recorded"}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {shifts.map((s) => {
              const c = s.present + s.absent;
              const pct = c ? Math.round((s.present / c) * 100) : null;
              return (
                <span
                  key={s.key}
                  className={`rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${SHIFT_STYLE[s.key].chip}`}
                >
                  {s.label}: {pct === null ? "no data" : `${pct}%`}
                </span>
              );
            })}
          </div>
        </div>

        <div className="hidden w-28 shrink-0 md:block">
          <p className="text-[11px] text-slate-400">Present / Absent</p>
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-emerald-600">{present}</span>
            <span className="text-slate-300"> · </span>
            <span className="font-semibold text-rose-600">{absent}</span>
            <span className="text-slate-400"> min</span>
          </p>
        </div>

        <div className="w-16 shrink-0 text-right">
          <p className="text-[11px] text-slate-400">Alerts</p>
          <p className="text-sm font-semibold text-slate-800">{alerts}</p>
        </div>

        <ChevronRight size={16} className="shrink-0 text-slate-300" />
      </button>
    </li>
  );
}

/* ---- Presence ------------------------------------------------------------ */

function Legend() {
  return (
    <div className="flex items-center gap-3 text-xs text-slate-500">
      {[["present", "Present"], ["absent", "Absent"], ["nodata", "No data"]].map(([k, label]) => (
        <span key={k} className="inline-flex items-center gap-1.5">
          <span className={`h-2.5 w-2.5 rounded-sm ${STATE_STYLE[k]}`} />
          {label}
        </span>
      ))}
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-white ring-2 ring-amber-400" />
        Alert
      </span>
    </div>
  );
}

function MinuteGrid({ log }) {
  return (
    <div>
      <div className="grid grid-cols-10 gap-1.5">
        {log.slots.map((s) => {
          const t = new Date(log.hourStart.getTime() + s.minute * 60000);
          const label = !s.inShift
            ? "Outside shift"
            : s.state === "nodata"
            ? "No data"
            : s.state === "present"
            ? "Present"
            : "Absent";
          return (
            <div
              key={s.minute}
              title={`${fmtTime(t)} · ${label}${s.alert ? " · ALERT" : ""}`}
              className={`relative flex aspect-square items-center justify-center rounded-lg text-[10px] font-medium transition hover:scale-110 ${
                STATE_STYLE[s.state]
              } ${s.state === "nodata" ? "text-slate-400" : "text-white"} ${s.inShift ? "" : "opacity-30"} ${
                s.alert ? "ring-2 ring-amber-400 ring-offset-1" : ""
              }`}
            >
              {pad(t.getUTCMinutes())}
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] text-slate-400">
        Numbers show the minute of the hour. Hover a tile for exact time.
        {log.cap < 60 && " Faded tiles are outside the shift."}
      </p>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <MiniStat label="Present" value={`${log.present} min`} tone="text-emerald-600" />
        <MiniStat label="Absent" value={`${log.absent} min`} tone="text-rose-600" />
        <MiniStat label="No data" value={`${log.noData} min`} tone="text-slate-500" />
        <MiniStat label="Alerts" value={log.alerts} tone="text-amber-600" />
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  return (
    <div className="rounded-xl bg-slate-50 px-3 py-2.5">
      <p className="text-xs text-slate-400">{label}</p>
      <p className={`text-lg font-semibold ${tone}`}>{value}</p>
    </div>
  );
}

function HourRow({ log, active, onClick }) {
  return (
    <li>
      <button
        onClick={onClick}
        className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 ${
          active ? "bg-sky-50 ring-1 ring-sky-200" : "hover:bg-slate-50"
        }`}
      >
        <div className="w-20 shrink-0">
          <p className="text-sm font-medium text-slate-700">{fmtTime(log.hourStart)}</p>
          <p className="text-xs text-slate-400">{fmtDay(log.hourStart)}</p>
        </div>

        <div className="flex h-5 min-w-0 flex-1 items-stretch gap-px overflow-hidden rounded-md">
          {log.slots.map((s) => (
            <span key={s.minute} className={`flex-1 ${STATE_STYLE[s.state]} ${s.inShift ? "" : "opacity-30"}`} />
          ))}
        </div>

        <div className="w-24 shrink-0 text-right">
          <p className="text-sm font-semibold text-emerald-600">{log.present} min</p>
          <p className="text-[11px] text-slate-400">{log.absent} min absent</p>
        </div>
      </button>
    </li>
  );
}

function Thumb({ src, className = "" }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed)
    return (
      <div className={`flex items-center justify-center bg-slate-100 text-slate-300 ${className}`}>
        <ImageOff size={18} />
      </div>
    );
  return (
    <img
      src={src}
      alt="Alert snapshot"
      loading="lazy"
      onError={() => setFailed(true)}
      className={`object-cover ${className}`}
    />
  );
}

function AlertItem({ alert, onClick }) {
  const meta = ALERT_TYPES[alert.type];
  const Icon = meta.icon;
  return (
    <li>
      <button
        onClick={onClick}
        className="flex w-full items-center gap-3 rounded-xl p-2 text-left transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
      >
        <Thumb src={alert.image} className="h-14 w-20 shrink-0 overflow-hidden rounded-lg border border-slate-200" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${meta.chip}`}>
              <Icon size={11} /> {meta.label}
            </span>
            <span className="shrink-0 text-[11px] text-slate-400">{timeAgo(alert.time)}</span>
          </div>
          <p className="mt-1 truncate text-xs text-slate-500">{alert.message}</p>
          <p className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-400">
            <Clock size={11} /> {fmtTime(alert.time)}
            {alert.shift && <span>· {SHIFT_LABEL[alert.shift]}</span>}
            {alert.channel != null && <span>· CAM {alert.channel}</span>}
            {alert.store !== "—" && <span className="truncate">· {alert.store}</span>}
          </p>
        </div>
      </button>
    </li>
  );
}

function AlertPreviewModal({ alert, onClose }) {
  const meta = ALERT_TYPES[alert.type];
  const Icon = meta.icon;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
          <div className="flex items-center gap-3">
            <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${meta.iconBox}`}>
              <Icon size={17} />
            </span>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">{meta.label}</h3>
              <p className="text-xs text-slate-400">
                {fmtDay(alert.time)}, {fmtTime(alert.time)} · {timeAgo(alert.time)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-400 transition hover:bg-slate-50"
          >
            <X size={16} />
          </button>
        </div>

        <div className="relative bg-slate-900">
          {alert.image ? (
            <>
              <img src={alert.image} alt="Alert snapshot" className="max-h-[70vh] w-full object-contain" />
              <a
                href={alert.image}
                target="_blank"
                rel="noreferrer"
                className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-lg bg-white/90 px-2.5 py-1 text-xs font-medium text-slate-600 shadow-sm backdrop-blur hover:bg-white"
              >
                <ExternalLink size={12} /> Open full size
              </a>
            </>
          ) : (
            <div className="flex aspect-video flex-col items-center justify-center gap-2 text-slate-400">
              <Camera size={26} />
              <span className="text-xs">No snapshot attached</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-5">
          <InfoTile label="Store" value={alert.store} />
          <InfoTile label="Shift" value={alert.shift ? SHIFT_LABEL[alert.shift] : "—"} />
          <InfoTile
            label="Camera"
            value={alert.channel != null ? `CAM ${alert.channel}` : "—"}
            sub={alert.cameraIp}
          />
          <InfoTile label="Use case" value={alert.useCase || meta.label} />
          <InfoTile
            label={alert.drawerState ? "Drawer state" : "Status"}
            value={alert.drawerState ? alert.drawerState.toUpperCase() : "Alert"}
          />
        </div>
      </div>
    </div>
  );
}

function InfoTile({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3.5 py-3">
      <p className="text-xs text-slate-400">{label}</p>
      <p className="mt-0.5 truncate text-sm font-semibold text-slate-800">{value}</p>
      {sub && <p className="truncate text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

/* ---- Shared states ------------------------------------------------------- */

function EmptyState({ text }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl bg-slate-50 py-10 text-center text-sm text-slate-400">
      <Inbox size={22} />
      <p className="max-w-xs">{text}</p>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-3">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-12 animate-pulse rounded-xl bg-slate-100" />
      ))}
    </div>
  );
}